/**
 * Repertoár – databáze písniček + playlisty (verze koncertů).
 *
 * On-screen pohled:
 *  - Bloky s písničkami: číslo · název · nástroje (oba muzikanti) · doba
 *  - Souhrn: počet písní, celková doba, pauzy mezi bloky
 *  - Admin: editace doby písničky inline (dvojklik)
 *
 * PDF export:
 *  - Vždy 1× A4, 2 sloupce, bez záhlaví/zápatí, jen název playlistu vpravo dole
 *  - Pouze nástroj vybraného muzikanta (Michal nebo Matouš)
 *  - Auto-fit velikosti písma — zmenšuje, dokud se vše nevejde na 1 stranu
 */
import { supabase } from '../lib/supabase.js';
import { isAdmin } from '../lib/auth.js';
import { openSongEditModal } from '../components/song-edit-modal.js';
import { exportElementToPdf } from '../lib/pdf-export.js';
import { openSongContextMenu } from '../components/song-context-menu.js';

// Konfigurace PDF — žádné záhlaví, jen drobný název playlistu vpravo dole
const PDF_MUSICIANS = [
    { id: 'michal', label: 'Michal' },
    { id: 'matous', label: 'Matouš' }
];
const PDF_PAGE_MARGIN_MM = 3;                              // minimální okraj
const PDF_PAGE_HEIGHT_MM = 297 - 2 * PDF_PAGE_MARGIN_MM;   // 291 mm
const PDF_PAGE_WIDTH_MM  = 210 - 2 * PDF_PAGE_MARGIN_MM;   // 204 mm

const PDF_FOOTER_HEIGHT_MM = 2;                            // rezerva: plocha A4 297 − 2×3 padding = 291; autofit cílí těsně pod ni (max. pokrytí papíru)
const PDF_FONT_PT_MAX = 22;       // dostatečně vysoký strop, ať autofit může jít nahoru
const PDF_FONT_PT_MIN = 4.5;      // a dolů pro extrémně husté playlisty
const PDF_FONT_PT_STEP = 0.1;     // jemný krok — autofit vyplní papír co nejtěsněji

// Estimovaná doba písně, pokud není zadána (3:30)
const DEFAULT_SONG_SEC = 210;

// Default přechodu mezi písněmi (potlesk, ladění, mluva).
// Lze přepsat v Nastavení (klíč 'song_overhead_sec').
const DEFAULT_OVERHEAD_SEC = 30;

// Settings cache pro tento render
let _songOverheadSec = DEFAULT_OVERHEAD_SEC;
let _bandName = '';

// Krátké zkratky pro on-screen zobrazení nástrojů
const INSTRUMENT_LABELS = { michal: 'M', matous: 'Mt', misa: 'Mš', tata: 'T' };

// Naváže na položky flag = "po této písni následuje změna nástroje".
// Iterujeme zpětně přes aktivní (nezskryté) bloky a sledujeme "další" nenull nástroj.
// Varování (podtržení) tak bude POD nástrojem na poslední písni před přepnutím.
function computeInstrumentChanges(blockView) {
    const allItems = [];
    blockView.forEach((block) => {
        if (block.isHidden) return;
        allItems.push(...block.items);
    });
    let nextMichal = null;
    let nextMatous = null;
    for (let i = allItems.length - 1; i >= 0; i--) {
        const it = allItems[i];
        const ins = (it.song && it.song.instruments) || {};
        const m  = ins.michal || null;
        const mt = ins.matous || null;
        // Warn = aktuální nástroj je definovaný, existuje další (non-null) nástroj a je odlišný
        it._changeMichal = !!(m  && nextMichal && m  !== nextMichal);
        it._changeMatous = !!(mt && nextMatous && mt !== nextMatous);
        if (m)  nextMichal = m;
        if (mt) nextMatous = mt;
    }
}

// Aktuálně vybraný muzikant pro PDF (persistuje mezi re-rendery v rámci sezení)
let pdfMusician = 'michal';

function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function fmtDuration(sec) {
    if (!sec || sec <= 0) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return s ? `${m}:${String(s).padStart(2, '0')}` : `${m} min`;
}

function fmtMin(min) {
    if (min == null) return '';
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
}

// Render nástrojů jako HTML — každý nástroj má vlastní barvu (přes data-instr).
function formatInstruments(obj) {
    if (!obj || typeof obj !== 'object') return '';
    const parts = [];
    const order = ['michal', 'matous', 'misa', 'tata'];
    const present = order.filter((k) => obj[k]);

    for (const k of present) {
        const v = String(obj[k]).trim();
        if (!v) continue;
        const label = present.length > 1 ? `${INSTRUMENT_LABELS[k]}: ${escapeHtml(v)}` : escapeHtml(v);
        parts.push(`<span class="ins-tok ins-${k}" data-instr="${escapeHtml(v)}">${label}</span>`);
    }
    for (const k of Object.keys(obj)) {
        if (order.includes(k) || k === 'drum') continue;
        const v = obj[k];
        if (!v) continue;
        parts.push(`<span class="ins-tok">${escapeHtml(String(v))}</span>`);
    }
    if (obj.drum === true || String(obj.drum).toUpperCase() === 'DRUM') {
        parts.push('<span class="ins-tok ins-drum">DRUM</span>');
    }
    return parts.join('<span class="ins-sep"> · </span>');
}

function calcBlockDuration(items) {
    // Každá píseň = její doba (nebo default) + overhead přechod mezi písněmi
    return items.reduce((s, it) => s + (it.song?.duration_sec || DEFAULT_SONG_SEC) + _songOverheadSec, 0);
}

// Pauza po bloku — řídí se pozicí v *aktivních* (nezskrytých) blocích.
// Skryté bloky se nezapočítávají, takže "každý 3. blok = dlouhá pauza"
// platí pro skutečnou pořadovou pozici v živém koncertu.
// Override z block_meta (per-block) má přednost; pak override z položky; pak default schema.
function pauseAfterActiveBlock(block, activeCount, playlist) {
    if (block.isHidden) return 0;
    const meta = getBlockMeta(playlist, block.originalIdx);
    if (meta && meta.pause_after_min != null) return meta.pause_after_min;
    const itemOverride = block.items[0]?.pause_after_min;
    if (itemOverride != null) return itemOverride;
    if (block.activeIdx >= activeCount - 1) return 0;
    const n = playlist.long_pause_every_n || 3;
    const isLong = ((block.activeIdx + 1) % n) === 0;
    return isLong ? (playlist.long_pause_min || 15) : (playlist.default_pause_min || 5);
}

function getBlockMeta(playlist, originalIdx) {
    const m = playlist.block_meta;
    if (!m || typeof m !== 'object') return null;
    return m[String(originalIdx)] || null;
}

// Vrátí pole bloků s metadaty {originalIdx, items, isHidden, isDiscard, activeIdx}.
// Blok "Vyřazené" (discard_block) je vždy přítomen — i prázdný.
function buildBlockView(rawBlocks, playlist) {
    const hiddenSet = new Set(playlist.hidden_blocks || []);
    const discardIdx = playlist.discard_block;
    // Doplň pole bloků až po discard index, ať blok Vyřazené existuje i prázdný
    const maxIdx = Math.max(rawBlocks.length - 1, discardIdx == null ? -1 : discardIdx);
    const blocks = [];
    for (let i = 0; i <= maxIdx; i++) blocks[i] = rawBlocks[i] || [];

    const view = blocks.map((items, i) => {
        const isDiscard = (discardIdx != null && i === discardIdx);
        return {
            originalIdx: i,
            items,
            isDiscard,
            // discard blok se chová jako skrytý (mimo PDF, souhrn, pauzy)
            isHidden: hiddenSet.has(i) || isDiscard,
            activeIdx: null
        };
    });
    let counter = 0;
    for (const b of view) if (!b.isHidden) b.activeIdx = counter++;
    return { view, activeCount: counter };
}

export async function renderRepertoir(container) {
    container.innerHTML = '<div class="state-banner">Načítám repertoár…</div>';
    const admin = await isAdmin();

    // Načti globální nastavení (overhead per píseň, jméno kapely)
    const { data: settingsRows } = await supabase.from('settings').select('key, value');
    const settings = Object.fromEntries((settingsRows || []).map((r) => [r.key, r.value]));
    _songOverheadSec = parseInt(settings.song_overhead_sec ?? String(DEFAULT_OVERHEAD_SEC), 10) || DEFAULT_OVERHEAD_SEC;
    _bandName = (settings.band_name || '').trim();

    const { data: playlists, error: plErr } = await supabase
        .from('playlists')
        .select('*')
        .order('created_at', { ascending: false });

    if (plErr) {
        container.innerHTML = `<div class="state-banner error">Chyba načtení playlistů: ${escapeHtml(plErr.message)}</div>`;
        return;
    }

    if (!playlists || playlists.length === 0) {
        container.innerHTML = `
            <div class="state-banner">
                Žádný playlist zatím není v databázi.
                Použij utilitu <code>docs/repertoir-import.html</code> pro import ze CSV nebo přidej ručně přes Supabase.
            </div>
        `;
        return;
    }

    const current = playlists.find((p) => p.is_current) || playlists[0];
    await renderPlaylist(container, current, playlists, admin);
}

async function renderPlaylist(container, playlist, allPlaylists, admin) {
    const { data: items, error } = await supabase
        .from('playlist_items')
        .select('*, song:songs(*)')
        .eq('playlist_id', playlist.id)
        .order('block_index', { ascending: true })
        .order('position', { ascending: true });

    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba načtení položek: ${escapeHtml(error.message)}</div>`;
        return;
    }

    // Seskup do bloků (raw — všechny, včetně skrytých)
    const rawBlocks = [];
    for (const it of (items || [])) {
        const idx = it.block_index;
        if (!rawBlocks[idx]) rawBlocks[idx] = [];
        rawBlocks[idx].push(it);
    }
    for (let i = 0; i < rawBlocks.length; i++) if (!rawBlocks[i]) rawBlocks[i] = [];

    // Zajisti, že playlist má blok "Vyřazené" (discard_block). Když chybí, založ jeho index.
    if (playlist.discard_block == null) {
        const discardIdx = rawBlocks.length; // nový volný index za posledním blokem
        const { error: dErr } = await supabase
            .from('playlists').update({ discard_block: discardIdx }).eq('id', playlist.id);
        if (!dErr) {
            playlist.discard_block = discardIdx;
            const pi = allPlaylists.findIndex((p) => p.id === playlist.id);
            if (pi >= 0) allPlaylists[pi] = { ...playlist };
        }
    }

    const { view: blockView, activeCount } = buildBlockView(rawBlocks, playlist);
    computeInstrumentChanges(blockView);

    // Kontrola duplicit — písnička se v repertoáru smí vyskytnout jen jednou.
    const songOccur = {};
    (items || []).forEach((it) => {
        if (it.song_id) songOccur[it.song_id] = (songOccur[it.song_id] || 0) + 1;
    });
    (items || []).forEach((it) => {
        it._isDuplicate = !!(it.song_id && songOccur[it.song_id] > 1);
    });
    const duplicateSongCount = Object.values(songOccur).filter((c) => c > 1).length;

    // Souhrny počítají jen aktivní (nezskryté) bloky
    let totalSec = 0;
    let totalPauseMin = 0;
    let hiddenSongs = 0;
    let hiddenBlocksCount = 0;
    blockView.forEach((b) => {
        if (b.isHidden) {
            hiddenSongs += b.items.length;
            hiddenBlocksCount++;
            return;
        }
        totalSec += calcBlockDuration(b.items);
        totalPauseMin += pauseAfterActiveBlock(b, activeCount, playlist);
    });
    const totalSongs = (items || []).length - hiddenSongs;
    const totalDurationMin = Math.round(totalSec / 60);
    const grandTotalMin = totalDurationMin + totalPauseMin;

    container.innerHTML = `
        <div class="repertoir-toolbar no-print">
            <label class="rep-select">
                Playlist:
                <select id="playlist-select">
                    ${allPlaylists.map((p) => `
                        <option value="${p.id}" ${p.id === playlist.id ? 'selected' : ''}>
                            ${escapeHtml(p.name)}${p.is_current ? ' · aktuální' : ''}${p.is_archived ? ' · archiv' : ''}
                        </option>
                    `).join('')}
                </select>
            </label>
            ${admin ? `
                <div class="rep-playlist-actions">
                    <button class="btn btn-sm" id="copy-playlist-btn" title="Vytvořit kopii s novým jménem">📋 Kopírovat</button>
                    <button class="btn btn-sm rep-btn-danger" id="delete-playlist-btn" title="Odstranit tento playlist (nevratné)">🗑 Odstranit</button>
                </div>
            ` : ''}
            <label class="rep-select">
                PDF pro:
                <select id="pdf-musician">
                    ${PDF_MUSICIANS.map((m) => `
                        <option value="${m.id}" ${m.id === pdfMusician ? 'selected' : ''}>${m.label}</option>
                    `).join('')}
                </select>
            </label>
            <div class="rep-stats">
                <strong>${totalSongs}</strong> písní ·
                <strong>${activeCount}</strong> bloků ·
                hraní <strong>${fmtMin(totalDurationMin)}</strong> ·
                pauzy <strong>${fmtMin(totalPauseMin)}</strong> ·
                celkem <strong>${fmtMin(grandTotalMin)}</strong>
                ${hiddenBlocksCount > 0 ? `<span class="rep-stats-reserve">+ ${hiddenBlocksCount} blok${hiddenBlocksCount === 1 ? '' : (hiddenBlocksCount < 5 ? 'y' : 'ů')} v záloze (${hiddenSongs} písní)</span>` : ''}
                ${duplicateSongCount > 0 ? `<span class="rep-stats-dup" title="Písnička je v repertoáru víckrát — ponech jen jednu">⚠ ${duplicateSongCount} duplicit${duplicateSongCount < 5 ? 'y' : ''}</span>` : ''}
            </div>
            <div class="rep-legend" title="Barva v plánovači = hlavní zpěvák písničky">
                <span class="rep-legend-chip" data-singer="misa">Míša</span>
                <span class="rep-legend-chip" data-singer="matous">Matouš</span>
                <span class="rep-legend-chip" data-singer="michal">Michal</span>
            </div>
            <div class="rep-actions">
                <button class="btn btn-sm rep-stage-btn" id="rep-stage-btn" title="Zobrazit playlist přes celou obrazovku" aria-label="Zobrazit na celou obrazovku">⛶ Na celou obrazovku</button>
                <label class="rep-pdf-opt" title="Do PDF zahrnout i bloky v záloze a blok Vyřazené">
                    <input type="checkbox" id="pdf-include-reserve"> Včetně zálohy a vyřazených
                </label>
                <button class="btn btn-sm btn-primary" id="print-btn" title="Černobílý tisk — 1× A4, 2 sloupce, nástroje">Tisk / PDF</button>
                <button class="btn btn-sm" id="export-pdf-btn" title="Barevný interaktivní PDF s prokliky na YouTube">🎬 Export PDF</button>
            </div>
        </div>

        <div class="repertoir-blocks no-print">
            <div class="rep-zone rep-zone-active" data-zone="active">
                ${blockView.filter((b) => !b.isHidden).map((b) => renderBlockHtml(b, activeCount, playlist, admin)).join('')}
            </div>
            <div class="rep-zone-divider">
                <span class="rep-zone-label">Záloha — vynecháno z PDF a souhrnu</span>
                <span class="rep-zone-hint">${admin ? 'Přetáhni sem blok myší, nebo použij tlačítko Schovat' : ''}</span>
            </div>
            <div class="rep-zone rep-zone-reserve" data-zone="reserve">
                ${(() => {
                    const reserve = blockView.filter((b) => b.isHidden && !b.isDiscard);
                    if (reserve.length === 0) {
                        return `<div class="rep-zone-empty">${admin ? 'Sem přetáhni blok, aby se vynechal z aktuálního repertoáru.' : 'Žádné záložní bloky.'}</div>`;
                    }
                    return reserve.map((b) => renderBlockHtml(b, activeCount, playlist, admin)).join('');
                })()}
            </div>
            <div class="rep-zone-divider rep-zone-divider-discard">
                <span class="rep-zone-label rep-zone-label-discard">Vyřazené — písně mimo repertoár</span>
            </div>
            <div class="rep-zone rep-zone-discard" data-zone="discard">
                ${(() => {
                    const discard = blockView.filter((b) => b.isDiscard);
                    if (discard.length === 0) {
                        return '<div class="rep-zone-empty">Blok Vyřazené ještě neexistuje.</div>';
                    }
                    return discard.map((b) => renderBlockHtml(b, activeCount, playlist, admin)).join('');
                })()}
            </div>
        </div>

        ${admin ? `
            <div class="footer-note no-print">
                <strong>Pravé tlačítko na písničku</strong> = rychlá změna nástrojů / zpěváka (barva).
                <strong>Dvojklik</strong> = plný editor (číslo, název, doba, nástroje, zpěvák).
                <strong>Dvojklik na název bloku</strong> = přejmenování + override pauzy.
                <strong>Tah myší z prázdna</strong> = lasso výběr.
                <strong>Tahem písně/výběru</strong> přesun mezi bloky.
                <strong>Tah za grip bloku</strong> přesune blok — spodní zóna = záloha.
                <em>Přechod mezi písněmi (${_songOverheadSec} s)</em> se nastavuje v Nastavení.
            </div>
        ` : ''}

        ${renderPrintAreaHtml(playlist, blockView.filter((b) => !b.isHidden).map((b) => b.items))}
        ${renderColorPrintAreaHtml(playlist, blockView.filter((b) => !b.isHidden).map((b) => b.items))}
    `;

    // Tiskové oblasti přesuneme jako přímé potomky <body>. Při tisku tak lze
    // zbytek aplikace skrýt přes display:none — jinak skrytý (visibility:hidden)
    // obsah dál zabírá místo a tiskne se mnoho prázdných stránek navíc.
    relocatePrintAreas(container);

    container.querySelector('#playlist-select').addEventListener('change', async (e) => {
        const id = e.target.value;
        const next = allPlaylists.find((p) => p.id === id);
        if (next) await renderPlaylist(container, next, allPlaylists, admin);
    });

    const copyBtn = container.querySelector('#copy-playlist-btn');
    if (copyBtn) copyBtn.addEventListener('click', () => copyPlaylist(container, playlist, admin));
    const delBtn = container.querySelector('#delete-playlist-btn');
    if (delBtn) delBtn.addEventListener('click', () => deletePlaylist(container, playlist, allPlaylists, admin));

    // Bloky pro PDF. Když je zapnutá záloha+vyřazené, přepne se layout do
    // víceřádkového režimu: 3 sloupce, běžná velikost písma, klidně víc stran.
    const pdfFooter = _bandName ? `${_bandName} · ${playlist.name}` : playlist.name;
    const pdfMode = () => {
        const full = container.querySelector('#pdf-include-reserve')?.checked || false;
        return {
            blocks: blockView.filter((b) => full || !b.isHidden).map((b) => b.items),
            multi: full
        };
    };

    container.querySelector('#pdf-musician').addEventListener('change', (e) => {
        pdfMusician = e.target.value;
        const { blocks, multi } = pdfMode();
        rebuildPrintArea(blocks, pdfFooter, multi);
    });

    container.querySelector('#pdf-include-reserve').addEventListener('change', () => {
        const { blocks, multi } = pdfMode();
        rebuildPrintArea(blocks, pdfFooter, multi);
        // barevný export — bez zápatí (čistě seznam písniček)
        rebuildColorPrintArea(blocks, '', multi);
    });

    container.querySelector('#print-btn').addEventListener('click', () => {
        document.body.classList.remove('rep-export-color');
        const { blocks, multi } = pdfMode();
        rebuildPrintArea(blocks, pdfFooter, multi);
        // Víceřádkový režim má pevné písmo z CSS — autofit jen pro 1× A4.
        if (!multi) autofitArea(container, 'rep-print-blocks');
        setTimeout(() => window.print(), 60);
    });

    const exportBtn = container.querySelector('#export-pdf-btn');
    if (exportBtn) exportBtn.addEventListener('click', async () => {
        const { blocks, multi } = pdfMode();
        rebuildColorPrintArea(blocks, '', multi);
        const area = document.getElementById('rep-print-area-color');
        const origText = exportBtn.textContent;
        exportBtn.disabled = true;
        exportBtn.textContent = 'Generuji PDF…';
        try {
            // Aplikace vyrobí PDF sama (obrázek stránky) — nezávisle na
            // tisku prohlížeče. Vždy přesně jedna stránka A4.
            await exportElementToPdf(area, 'repertoar-amati.pdf');
        } catch (err) {
            alert('Export PDF se nepodařil: ' + (err?.message || err));
        } finally {
            exportBtn.disabled = false;
            exportBtn.textContent = origText;
        }
    });

    container.querySelector('#rep-stage-btn')?.addEventListener('click', () => {
        openStageView(blockView, playlist.name);
    });

    if (admin) {
        container.querySelectorAll('.rep-song[data-song-id]').forEach((li) => {
            li.addEventListener('dblclick', (e) => {
                if (li.classList.contains('rep-dragging')) return;
                window.getSelection?.()?.removeAllRanges();
                e.preventDefault();
                const itemId = li.dataset.itemId;
                const ol = li.closest('.rep-songs');
                const blockIdx = ol ? parseInt(ol.dataset.blockIdx, 10) : null;
                const inDiscard = playlist.discard_block != null && blockIdx === playlist.discard_block;
                openSongEditModal(
                    li.dataset.songId,
                    async () => { await renderPlaylist(container, playlist, allPlaylists, admin); },
                    inDiscard
                        ? {
                            discardLabel: 'Vrátit do 1. bloku',
                            onDiscard: async () => {
                                await moveSongItemToBlock(itemId, 0, playlist, container, allPlaylists, admin);
                            }
                          }
                        : {
                            discardLabel: 'Vyřadit',
                            onDiscard: async () => {
                                await discardSongItem(itemId, playlist, container, allPlaylists, admin);
                            }
                          }
                );
            });
            // Pravé tlačítko = rychlé kontextové menu
            li.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                openSongContextMenu(e.clientX, e.clientY, li.dataset.songId, async () => {
                    await renderPlaylist(container, playlist, allPlaylists, admin);
                });
            });
        });
        container.querySelectorAll('[data-toggle-hidden]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const idx = parseInt(btn.dataset.toggleHidden, 10);
                toggleBlockHidden(playlist, idx, container, allPlaylists, admin);
            });
        });
        // Mapa block_index → zobrazovaný název (pro hlášku "přesunuto z bloku X")
        const blockNames = {};
        blockView.forEach((b) => {
            const bMeta = getBlockMeta(playlist, b.originalIdx) || {};
            blockNames[b.originalIdx] = b.isDiscard ? 'Vyřazené'
                : (bMeta.name || (b.isHidden ? 'Záloha' : `Blok ${b.activeIdx + 1}`));
        });
        container.querySelectorAll('[data-add-song]').forEach((btn) => {
            btn.addEventListener('click', () => {
                const idx = parseInt(btn.dataset.addSong, 10);
                const block = blockView.find((b) => b.originalIdx === idx);
                openSongPicker(playlist, idx, block ? block.items.length : 0, blockNames, container, allPlaylists, admin);
            });
        });
        container.querySelectorAll('[data-edit-block-name]').forEach((h3) => {
            h3.addEventListener('dblclick', (e) => {
                e.preventDefault();
                e.stopPropagation();
                window.getSelection?.()?.removeAllRanges();
                const idx = parseInt(h3.dataset.editBlockName, 10);
                openBlockMetaEditor(h3, idx, container, playlist, allPlaylists, admin);
            });
        });
        attachDragAndDrop(container, playlist, allPlaylists, admin);
    }
}

function openBlockMetaEditor(h3, originalIdx, container, playlist, allPlaylists, admin) {
    if (h3.querySelector('.rep-block-edit')) return; // už otevřeno

    const meta = getBlockMeta(playlist, originalIdx) || {};
    const currentName = meta.name || '';
    const currentPause = meta.pause_after_min != null ? String(meta.pause_after_min) : '';

    const wrap = document.createElement('span');
    wrap.className = 'rep-block-edit';
    wrap.innerHTML = `
        <input class="rbe-name" type="text" placeholder="Název bloku" value="${escapeHtml(currentName)}" maxlength="60">
        <input class="rbe-pause" type="number" placeholder="auto" min="0" max="999" value="${escapeHtml(currentPause)}" title="Pauza po bloku v minutách (prázdné = auto podle pravidla)">
        <span class="rbe-pause-suffix">min</span>
        <button class="rbe-save" type="button" title="Uložit (Enter)">✓</button>
        <button class="rbe-cancel" type="button" title="Zrušit (Esc)">✕</button>
    `;

    const original = h3.innerHTML;
    h3.innerHTML = '';
    h3.appendChild(wrap);
    const nameInput = wrap.querySelector('.rbe-name');
    nameInput.focus();
    nameInput.select();

    const close = (revert) => {
        if (revert) h3.innerHTML = original;
        wrap.remove();
    };

    const save = async () => {
        const nameVal = nameInput.value.trim();
        const pauseVal = wrap.querySelector('.rbe-pause').value.trim();
        const newMeta = { ...(playlist.block_meta || {}) };
        const key = String(originalIdx);
        const next = {};
        if (nameVal) next.name = nameVal;
        if (pauseVal !== '') {
            const n = parseInt(pauseVal, 10);
            if (!isNaN(n) && n >= 0) next.pause_after_min = n;
        }
        if (Object.keys(next).length === 0) delete newMeta[key];
        else newMeta[key] = next;

        const { error } = await supabase
            .from('playlists')
            .update({ block_meta: newMeta })
            .eq('id', playlist.id);
        if (error) {
            alert('Uložení selhalo: ' + error.message);
            close(true);
            return;
        }
        playlist.block_meta = newMeta;
        const i = allPlaylists.findIndex((p) => p.id === playlist.id);
        if (i >= 0) allPlaylists[i] = { ...playlist };
        await renderPlaylist(container, playlist, allPlaylists, admin);
    };

    wrap.querySelector('.rbe-save').addEventListener('click', save);
    wrap.querySelector('.rbe-cancel').addEventListener('click', () => close(true));
    wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { e.preventDefault(); save(); }
        else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    });
}

// ===== Drag & drop písniček a bloků (admin) =====
let _draggingEl = null;        // primární tahaná písnička <li>
let _draggingSelection = null; // pole VŠECH tažených písniček (multi-select)
let _draggingBlock = null;     // tahaný blok <section>
let _openedForDrag = [];       // sbalené bloky dočasně otevřené kvůli dragu

// Esc kdekoli zruší výběr písniček. Vážeme jednou globálně.
if (typeof window !== 'undefined' && !window._repertoirEscBound) {
    window._repertoirEscBound = true;
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            document.querySelectorAll('.rep-song-selected').forEach((el) => el.classList.remove('rep-song-selected'));
        }
    });
}

function attachDragAndDrop(container, playlist, allPlaylists, admin) {
    // Písničky — drag handlers
    container.querySelectorAll('.repertoir-blocks .rep-song.is-draggable').forEach((li) => {
        li.addEventListener('dragstart', (e) => onSongDragStart(e, container));
        li.addEventListener('dragend', (e) => onSongDragEnd(e, container, playlist, allPlaylists, admin));
        // Cmd/Ctrl/Shift+Click přepne výběr písničky
        li.addEventListener('click', (e) => {
            if (!(e.metaKey || e.ctrlKey || e.shiftKey)) return;
            e.preventDefault();
            e.stopPropagation();
            li.classList.toggle('rep-song-selected');
        });
    });
    container.querySelectorAll('.repertoir-blocks .rep-songs').forEach((ol) => {
        ol.addEventListener('dragover', onSongDragOver);
        ol.addEventListener('drop', (e) => e.preventDefault());
    });

    // Bloky (drag jen za grip)
    container.querySelectorAll('.repertoir-blocks .rep-block-grip').forEach((grip) => {
        grip.addEventListener('dragstart', onBlockDragStart);
        grip.addEventListener('dragend', (e) => onBlockDragEnd(e, container, playlist, allPlaylists, admin));
    });
    // Bloky lze pouštět jen do aktivní a záložní zóny (ne do Vyřazené)
    container.querySelectorAll('.repertoir-blocks .rep-zone-active, .repertoir-blocks .rep-zone-reserve').forEach((zone) => {
        zone.addEventListener('dragover', onBlockZoneDragOver);
        zone.addEventListener('drop', (e) => e.preventDefault());
    });

    // Marquee (lasso) výběr písniček
    attachMarqueeSelection(container);
}

// ---- Marquee (lasso) výběr ----

function attachMarqueeSelection(container) {
    const area = container.querySelector('.repertoir-blocks');
    if (!area) return;
    area.addEventListener('mousedown', (e) => startMarquee(e, container));
}

function startMarquee(e, container) {
    if (e.button !== 0) return; // jen levé tlačítko
    // Pokud kliknul na interaktivní prvek — nech to být
    if (e.target.closest('.rep-song, .rep-block-grip, button, input, select, textarea, label, a')) {
        return;
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;

    // Zachovej dosavadní výběr pokud additive; jinak hned po dragu očistíme
    const previousSelection = additive
        ? new Set(container.querySelectorAll('.rep-song-selected'))
        : new Set();

    let marquee = null;
    let moved = false;

    const onMove = (ev) => {
        if (!moved) {
            if (Math.abs(ev.clientX - startX) < 4 && Math.abs(ev.clientY - startY) < 4) return;
            moved = true;
            // Až tady (po prokázané intenci dragu) zruš starý výběr
            if (!additive) {
                container.querySelectorAll('.rep-song-selected').forEach((el) => el.classList.remove('rep-song-selected'));
            }
            marquee = document.createElement('div');
            marquee.className = 'rep-marquee';
            document.body.appendChild(marquee);
        }
        updateMarqueeRect(marquee, startX, startY, ev.clientX, ev.clientY);
        updateMarqueeSelection(container, marquee, previousSelection);
    };

    const onUp = (ev) => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        if (marquee) { marquee.remove(); marquee = null; }
        if (!moved && !additive) {
            // Čistý klik na prázdno = zruš výběr
            container.querySelectorAll('.rep-song-selected').forEach((el) => el.classList.remove('rep-song-selected'));
        }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
}

function updateMarqueeRect(el, x1, y1, x2, y2) {
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    el.style.left = left + 'px';
    el.style.top = top + 'px';
    el.style.width = Math.abs(x2 - x1) + 'px';
    el.style.height = Math.abs(y2 - y1) + 'px';
}

function updateMarqueeSelection(container, marqueeEl, previousSelection) {
    const m = marqueeEl.getBoundingClientRect();
    container.querySelectorAll('.rep-song[data-song-id]').forEach((li) => {
        const b = li.getBoundingClientRect();
        const overlaps = !(b.right < m.left || b.left > m.right || b.bottom < m.top || b.top > m.bottom);
        if (overlaps || previousSelection.has(li)) li.classList.add('rep-song-selected');
        else li.classList.remove('rep-song-selected');
    });
}

// ---- Písničky ----

function onSongDragStart(e, container) {
    _draggingEl = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', _draggingEl.dataset.itemId); } catch (_) {}
    e.stopPropagation();

    // Pokud táhneme jednu z vybraných písní → group drag.
    // Jinak zruš výběr a táhni jen tento prvek.
    const selected = [...container.querySelectorAll('.rep-song.rep-song-selected')];
    if (_draggingEl.classList.contains('rep-song-selected') && selected.length > 1) {
        // querySelectorAll vrací v DOM pořadí — zachovává relativní pořadí písní napříč bloky
        _draggingSelection = selected;
    } else {
        container.querySelectorAll('.rep-song-selected').forEach((el) => el.classList.remove('rep-song-selected'));
        _draggingSelection = [_draggingEl];
    }
    _draggingSelection.forEach((el) => el.classList.add('rep-dragging'));

    // Rozbal všechny sbalené bloky (Záloha, Vyřazené), ať jde písničku pustit i do nich
    _openedForDrag = [...container.querySelectorAll('.rep-block-collapse:not([open])')];
    _openedForDrag.forEach((d) => { d.open = true; });
}

function onSongDragOver(e) {
    if (!_draggingEl || !_draggingSelection) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const ol = e.currentTarget;

    // Najdi prvek *před který* (kromě tažených) se má skupina vložit.
    const draggingSet = new Set(_draggingSelection);
    let insertBefore = null;
    for (const child of ol.children) {
        if (!child.classList || !child.classList.contains('rep-song')) continue;
        if (draggingSet.has(child)) continue;
        const box = child.getBoundingClientRect();
        if (e.clientY < box.top + box.height / 2) {
            insertBefore = child;
            break;
        }
    }

    // Vlož všechny tažené v zachyceném DOM pořadí.
    for (const el of _draggingSelection) {
        if (insertBefore == null) {
            if (ol.lastElementChild !== el) ol.appendChild(el);
        } else if (el !== insertBefore) {
            ol.insertBefore(el, insertBefore);
        }
    }
}

async function onSongDragEnd(e, container, playlist, allPlaylists, admin) {
    const selection = _draggingSelection;
    _draggingEl = null;
    _draggingSelection = null;
    if (selection) selection.forEach((el) => el.classList.remove('rep-dragging'));

    // Sbalené bloky, které jsme kvůli dragu otevřeli — zavřeme zpět (re-render je stejně zruší)
    const restoreCollapsed = () => {
        _openedForDrag.forEach((d) => { if (d.isConnected) d.open = false; });
        _openedForDrag = [];
    };

    if (!e.dataTransfer || e.dataTransfer.dropEffect === 'none') {
        _openedForDrag = [];
        await renderPlaylist(container, playlist, allPlaylists, admin);
        return;
    }

    const updates = collectSongChanges(container);
    if (updates.length === 0) { restoreCollapsed(); return; }
    _openedForDrag = [];

    for (const u of updates) {
        const { error } = await supabase
            .from('playlist_items')
            .update({ block_index: u.block_index, position: u.position })
            .eq('id', u.id);
        if (error) {
            alert('Uložení pořadí selhalo: ' + error.message);
            break;
        }
    }
    await renderPlaylist(container, playlist, allPlaylists, admin);
}

function collectSongChanges(container) {
    const updates = [];
    container.querySelectorAll('.repertoir-blocks .rep-songs').forEach((ol) => {
        const newBlock = parseInt(ol.dataset.blockIdx, 10);
        [...ol.querySelectorAll('.rep-song')].forEach((li, newPos) => {
            const origBlock = parseInt(li.dataset.originalBlock, 10);
            const origPos = parseInt(li.dataset.originalPosition, 10);
            if (newBlock !== origBlock || newPos !== origPos) {
                updates.push({
                    id: li.dataset.itemId,
                    block_index: newBlock,
                    position: newPos
                });
            }
        });
    });
    return updates;
}

// ---- Bloky ----

function onBlockDragStart(e) {
    const section = e.currentTarget.closest('.rep-block');
    if (!section) return;
    _draggingBlock = section;
    section.classList.add('rep-block-dragging');
    e.dataTransfer.effectAllowed = 'move';
    // Použij celý blok jako náhled při tažení
    try { e.dataTransfer.setDragImage(section, 24, 18); } catch (_) {}
    try { e.dataTransfer.setData('text/plain', 'block:' + section.dataset.blockOriginalIdx); } catch (_) {}
}

function onBlockZoneDragOver(e) {
    if (!_draggingBlock) return; // pouze pokud táháme blok
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const zone = e.currentTarget;
    zone.classList.add('rep-zone-drop-target');

    // Smaž případnou prázdnou zprávu, kdyby tam ještě byla
    const empty = zone.querySelector('.rep-zone-empty');
    if (empty) empty.remove();

    // Najdi blok, *před který* se má vložit (podle Y v rámci zóny).
    // V CSS-columns layoutu může být pořadí přibližné, ale postačí.
    const blocks = [...zone.querySelectorAll(':scope > .rep-block:not(.rep-block-dragging)')];
    let inserted = false;
    for (const block of blocks) {
        const box = block.getBoundingClientRect();
        if (e.clientY < box.top + box.height / 2) {
            if (_draggingBlock.parentNode !== zone || _draggingBlock.nextSibling !== block) {
                zone.insertBefore(_draggingBlock, block);
            }
            inserted = true;
            break;
        }
    }
    if (!inserted && zone.lastElementChild !== _draggingBlock) {
        zone.appendChild(_draggingBlock);
    }
}

async function onBlockDragEnd(e, container, playlist, allPlaylists, admin) {
    const section = _draggingBlock;
    _draggingBlock = null;
    if (section) section.classList.remove('rep-block-dragging');
    container.querySelectorAll('.rep-zone-drop-target').forEach((z) => z.classList.remove('rep-zone-drop-target'));

    if (!e.dataTransfer || e.dataTransfer.dropEffect === 'none') {
        await renderPlaylist(container, playlist, allPlaylists, admin);
        return;
    }

    await persistBlockOrder(container, playlist, allPlaylists, admin);
}

async function persistBlockOrder(container, playlist, allPlaylists, admin) {
    const activeZone = container.querySelector('.rep-zone-active');
    const reserveZone = container.querySelector('.rep-zone-reserve');
    const discardZone = container.querySelector('.rep-zone-discard');
    if (!activeZone || !reserveZone) return;

    const activeSections = [...activeZone.querySelectorAll(':scope > .rep-block')];
    const reserveSections = [...reserveZone.querySelectorAll(':scope > .rep-block')];
    const discardSections = discardZone ? [...discardZone.querySelectorAll(':scope > .rep-block')] : [];

    // Nový block_index = pořadí v DOM (aktivní, pak záložní, pak Vyřazené úplně poslední)
    const allSections = [...activeSections, ...reserveSections, ...discardSections];
    const newOrder = allSections.map((sec) => parseInt(sec.dataset.blockOriginalIdx, 10));

    // Hidden = záložní zóna; Discard = blok Vyřazené (vždy poslední)
    const newHidden = [];
    for (let i = activeSections.length; i < activeSections.length + reserveSections.length; i++) newHidden.push(i);
    const newDiscard = discardSections.length
        ? activeSections.length + reserveSections.length
        : playlist.discard_block;

    // Stará konfigurace pro porovnání
    const oldHidden = (playlist.hidden_blocks || []).slice().sort((a, b) => a - b);
    const newHiddenSorted = newHidden.slice().sort((a, b) => a - b);

    const orderChanged = !newOrder.every((idx, i) => idx === i);
    const hiddenChanged = oldHidden.length !== newHiddenSorted.length
        || oldHidden.some((v, i) => v !== newHiddenSorted[i]);
    const discardChanged = newDiscard !== playlist.discard_block;

    if (!orderChanged && !hiddenChanged && !discardChanged) return;

    // Update block_index na playlist_items (pokud se přečíslovalo)
    if (orderChanged) {
        const { data: items, error: loadErr } = await supabase
            .from('playlist_items')
            .select('id, block_index')
            .eq('playlist_id', playlist.id);
        if (loadErr) {
            alert('Načtení položek selhalo: ' + loadErr.message);
            await renderPlaylist(container, playlist, allPlaylists, admin);
            return;
        }

        const idsByOldBlock = new Map();
        items.forEach((it) => {
            if (!idsByOldBlock.has(it.block_index)) idsByOldBlock.set(it.block_index, []);
            idsByOldBlock.get(it.block_index).push(it.id);
        });

        const mapping = new Map();
        newOrder.forEach((oldIdx, newIdx) => mapping.set(oldIdx, newIdx));

        for (const [oldIdx, newIdx] of mapping.entries()) {
            if (oldIdx === newIdx) continue;
            const ids = idsByOldBlock.get(oldIdx);
            if (!ids?.length) continue;
            const { error } = await supabase
                .from('playlist_items')
                .update({ block_index: newIdx })
                .in('id', ids);
            if (error) {
                alert('Přečíslování bloků selhalo: ' + error.message);
                await renderPlaylist(container, playlist, allPlaylists, admin);
                return;
            }
        }
    }

    // Pro přemapování block_meta potřebujeme starý → nový index
    const mappingForMeta = new Map();
    newOrder.forEach((oldIdx, newIdx) => mappingForMeta.set(oldIdx, newIdx));

    // Přemapuj block_meta (název + pauza per blok)
    const newBlockMeta = {};
    const oldBlockMeta = (playlist.block_meta && typeof playlist.block_meta === 'object') ? playlist.block_meta : {};
    for (const [oldKey, value] of Object.entries(oldBlockMeta)) {
        const oldIdx = parseInt(oldKey, 10);
        const newIdx = mappingForMeta.get(oldIdx);
        if (newIdx != null) newBlockMeta[String(newIdx)] = value;
    }
    const metaChanged = JSON.stringify(newBlockMeta) !== JSON.stringify(oldBlockMeta);

    // Update hidden_blocks + block_meta + discard_block, pokud se něco změnilo
    if (hiddenChanged || orderChanged || metaChanged || discardChanged) {
        const updatePayload = { hidden_blocks: newHiddenSorted, discard_block: newDiscard };
        if (metaChanged) updatePayload.block_meta = newBlockMeta;
        const { error: hErr } = await supabase
            .from('playlists')
            .update(updatePayload)
            .eq('id', playlist.id);
        if (hErr) {
            alert('Aktualizace metadat playlistu selhala: ' + hErr.message);
        } else {
            playlist.hidden_blocks = newHiddenSorted;
            playlist.block_meta = newBlockMeta;
            playlist.discard_block = newDiscard;
            const i = allPlaylists.findIndex((p) => p.id === playlist.id);
            if (i >= 0) allPlaylists[i] = { ...playlist };
        }
    }

    await renderPlaylist(container, playlist, allPlaylists, admin);
}

// Vrátí prvek, *před který* by se mělo drag-prvek vložit. null = na konec.
function nextSiblingForY(parent, y, selector) {
    const siblings = [...parent.querySelectorAll(selector)];
    for (const el of siblings) {
        const box = el.getBoundingClientRect();
        if (y < box.top + box.height / 2) return el;
    }
    return null;
}

async function copyPlaylist(container, source, admin) {
    const defaultName = `${source.name} (kopie)`;
    const newName = window.prompt('Název nového playlistu:', defaultName);
    if (newName == null) return;          // Storno
    const trimmed = newName.trim();
    if (!trimmed) { alert('Název nesmí být prázdný.'); return; }

    // 1) Vytvoř kopii playlistu (bez příznaku "aktuální")
    const { data: newPl, error: e1 } = await supabase.from('playlists').insert({
        name: trimmed,
        is_current: false,
        is_archived: false,
        default_pause_min:   source.default_pause_min,
        long_pause_every_n:  source.long_pause_every_n,
        long_pause_min:      source.long_pause_min,
        hidden_blocks:       source.hidden_blocks || [],
        block_meta:          source.block_meta || {},
        discard_block:       source.discard_block ?? null
    }).select().single();
    if (e1) { alert('Kopírování playlistu selhalo: ' + e1.message); return; }

    // 2) Načti položky původního playlistu
    const { data: items, error: e2 } = await supabase
        .from('playlist_items')
        .select('song_id, block_index, position, pause_after_min, notes')
        .eq('playlist_id', source.id);
    if (e2) { alert('Chyba čtení položek: ' + e2.message); return; }

    // 3) Vlož kopie s novým playlist_id
    if (items && items.length > 0) {
        const copies = items.map((it) => ({ ...it, playlist_id: newPl.id }));
        const { error: e3 } = await supabase.from('playlist_items').insert(copies);
        if (e3) { alert('Chyba kopírování položek: ' + e3.message); return; }
    }

    // 4) Načti seznam playlistů a přepni rovnou na nový
    const { data: playlists } = await supabase.from('playlists').select('*')
        .order('created_at', { ascending: false });
    const next = (playlists || []).find((p) => p.id === newPl.id);
    if (next) await renderPlaylist(container, next, playlists, admin);
    else await renderRepertoir(container);
}

async function deletePlaylist(container, playlist, allPlaylists, admin) {
    const itemCount = (await supabase.from('playlist_items').select('id', { count: 'exact', head: true })
        .eq('playlist_id', playlist.id)).count || 0;
    const confirmMsg = `Opravdu odstranit playlist "${playlist.name}"?\n\n`
        + `Smaže ${itemCount} položek (písničky v databázi zůstanou).\nTato akce je nevratná.`;
    if (!confirm(confirmMsg)) return;

    const { error } = await supabase.from('playlists').delete().eq('id', playlist.id);
    if (error) { alert('Odstranění selhalo: ' + error.message); return; }

    // Odebereme z lokálního pole a přepneme na další (nebo prázdný stav)
    const remaining = allPlaylists.filter((p) => p.id !== playlist.id);
    if (remaining.length > 0) {
        await renderPlaylist(container, remaining[0], remaining, admin);
    } else {
        await renderRepertoir(container);
    }
}

// Přesun položky playlistu do daného bloku (na konec bloku)
async function moveSongItemToBlock(itemId, targetBlockIdx, playlist, container, allPlaylists, admin) {
    const { count } = await supabase
        .from('playlist_items')
        .select('id', { count: 'exact', head: true })
        .eq('playlist_id', playlist.id)
        .eq('block_index', targetBlockIdx);
    const { error } = await supabase
        .from('playlist_items')
        .update({ block_index: targetBlockIdx, position: count || 0 })
        .eq('id', itemId);
    if (error) throw error;
    await renderPlaylist(container, playlist, allPlaylists, admin);
}

// Přesun položky do bloku Vyřazené
async function discardSongItem(itemId, playlist, container, allPlaylists, admin) {
    if (playlist.discard_block == null) {
        throw new Error('Blok Vyřazené v tomto playlistu neexistuje.');
    }
    await moveSongItemToBlock(itemId, playlist.discard_block, playlist, container, allPlaylists, admin);
}

// Výběr písničky z celé databáze repertoáru → vložení do bloku.
// Pokud písnička v playlistu už je, PŘESUNE se z původního bloku (žádné duplicity).
async function openSongPicker(playlist, blockOriginalIdx, endPosition, blockNames, container, allPlaylists, admin) {
    const { data: songs, error } = await supabase
        .from('songs')
        .select('id, song_number, title')
        .order('song_number', { ascending: true });
    if (error) { alert('Načtení písní selhalo: ' + error.message); return; }

    // Mapa song_id → { itemId, blockIndex } — kde je písnička právě teď v playlistu
    const { data: usedRows } = await supabase
        .from('playlist_items').select('id, song_id, block_index').eq('playlist_id', playlist.id);
    const usedMap = new Map();
    (usedRows || []).forEach((r) => usedMap.set(r.song_id, { itemId: r.id, blockIndex: r.block_index }));

    let m = document.getElementById('song-picker-modal');
    if (m) m.remove();
    m = document.createElement('div');
    m.id = 'song-picker-modal';
    m.className = 'modal-overlay open';
    m.innerHTML = `
        <div class="modal-window song-picker-window">
            <div class="modal-head">
                <h2>Vložit písničku do bloku</h2>
                <button class="modal-close" type="button">×</button>
            </div>
            <div class="modal-body">
                <div style="display:flex;gap:8px;margin-bottom:8px;">
                    <input type="search" id="sp-search" placeholder="Hledat číslo nebo název písničky…"
                           style="flex:1;padding:8px 10px;border:1px solid var(--border);border-radius:4px;font-size:14px;">
                    <button class="btn btn-primary btn-sm" id="sp-new" type="button" title="Vytvořit úplně novou písničku">+ Nová písnička</button>
                </div>
                <div class="sp-list" id="sp-list"></div>
            </div>
        </div>
    `;
    document.body.appendChild(m);

    const listEl = m.querySelector('#sp-list');
    const renderList = (filter) => {
        const f = (filter || '').trim().toLowerCase();
        const filtered = !f ? songs : songs.filter((s) =>
            String(s.song_number || '').includes(f) || (s.title || '').toLowerCase().includes(f));
        listEl.innerHTML = filtered.slice(0, 250).map((s) => {
            const used = usedMap.get(s.id);
            const usedTag = used
                ? ` <span class="sp-used-tag">→ z bloku „${escapeHtml(blockNames[used.blockIndex] || ('blok ' + used.blockIndex))}"</span>`
                : '';
            return `<div class="sp-item${used ? ' sp-used' : ''}" data-song="${s.id}"
                         title="${used ? 'Písnička se přesune ze svého bloku sem' : 'Klikni pro vložení'}">
                <span class="sp-num">${s.song_number ?? ''}</span>
                <span class="sp-title">${escapeHtml(s.title || '')}${usedTag}</span>
            </div>`;
        }).join('') || '<div style="padding:14px;color:var(--text-muted);font-size:13px;">Nic nenalezeno.</div>';
        listEl.querySelectorAll('.sp-item[data-song]').forEach((el) => {
            el.addEventListener('click', () => pick(el.dataset.song));
        });
    };

    const close = () => m.remove();
    const pick = async (songId) => {
        const existing = usedMap.get(songId);
        if (existing) {
            // Písnička už v playlistu je → přesun (žádná duplikace)
            const fromName = blockNames[existing.blockIndex] || ('blok ' + existing.blockIndex);
            const { error: mvErr } = await supabase.from('playlist_items')
                .update({ block_index: blockOriginalIdx, position: endPosition })
                .eq('id', existing.itemId);
            if (mvErr) { alert('Přesun selhal: ' + mvErr.message); return; }
            close();
            alert(`Písnička byla přesunuta z bloku „${fromName}" do tohoto bloku.`);
            await renderPlaylist(container, playlist, allPlaylists, admin);
            return;
        }
        // Nová písnička → vložit
        const { error: insErr } = await supabase.from('playlist_items').insert({
            playlist_id: playlist.id,
            song_id: songId,
            block_index: blockOriginalIdx,
            position: endPosition
        });
        if (insErr) { alert('Vložení selhalo: ' + insErr.message); return; }
        close();
        await renderPlaylist(container, playlist, allPlaylists, admin);
    };

    m.querySelector('.modal-close').addEventListener('click', close);
    m.addEventListener('click', (e) => { if (e.target === m) close(); });
    document.addEventListener('keydown', function onEsc(e) {
        if (e.key === 'Escape') { close(); document.removeEventListener('keydown', onEsc); }
    });

    // Vytvořit úplně novou písničku → po vytvoření ji rovnou vlož do bloku
    m.querySelector('#sp-new').addEventListener('click', () => {
        close();
        openSongEditModal(null, async (newSongId) => {
            if (newSongId) {
                const { error: insErr } = await supabase.from('playlist_items').insert({
                    playlist_id: playlist.id,
                    song_id: newSongId,
                    block_index: blockOriginalIdx,
                    position: endPosition
                });
                if (insErr) { alert('Vložení nové písničky selhalo: ' + insErr.message); }
            }
            await renderPlaylist(container, playlist, allPlaylists, admin);
        });
    });

    const search = m.querySelector('#sp-search');
    search.addEventListener('input', () => renderList(search.value));
    renderList('');
    setTimeout(() => search.focus(), 50);
}

async function toggleBlockHidden(playlist, blockIndex, container, allPlaylists, admin) {
    const current = new Set(playlist.hidden_blocks || []);
    if (current.has(blockIndex)) current.delete(blockIndex);
    else current.add(blockIndex);
    const updated = [...current].sort((a, b) => a - b);

    const { error } = await supabase
        .from('playlists')
        .update({ hidden_blocks: updated })
        .eq('id', playlist.id);

    if (error) {
        alert('Uložení selhalo: ' + error.message);
        return;
    }

    // Aktualizuj lokální stav (i v allPlaylists, aby přepínání mezi playlisty bylo konzistentní)
    const updatedPlaylist = { ...playlist, hidden_blocks: updated };
    const i = allPlaylists.findIndex((p) => p.id === playlist.id);
    if (i >= 0) allPlaylists[i] = updatedPlaylist;

    await renderPlaylist(container, updatedPlaylist, allPlaylists, admin);
}

function renderBlockHtml(block, activeCount, playlist, admin) {
    const { items, isHidden, isDiscard, activeIdx, originalIdx } = block;
    const sec = calcBlockDuration(items);
    const pauseMin = isHidden ? 0 : pauseAfterActiveBlock(block, activeCount, playlist);
    const isLong = pauseMin >= (playlist.long_pause_min || 15);
    const meta = getBlockMeta(playlist, originalIdx) || {};
    const defaultTitle = isDiscard ? 'Vyřazené' : (isHidden ? 'Záloha' : `Blok ${activeIdx + 1}`);
    const customName = (!isDiscard && meta.name) ? escapeHtml(meta.name) : '';
    const title = customName || defaultTitle;
    const hasCustomPause = meta.pause_after_min != null;
    const badge = isDiscard ? '<span class="rep-discard-badge">VYŘAZENÉ</span>'
        : (isHidden ? '<span class="rep-reserve-badge">ZÁLOHA</span>' : '');
    const titleHtml = (admin && !isDiscard)
        ? `<h3 class="rep-block-title${customName ? ' has-custom-name' : ''}"
                data-edit-block-name="${originalIdx}"
                title="Dvojklik = přejmenovat blok a změnit pauzu">${title}${badge}${hasCustomPause ? '<span class="rep-pause-custom-mark" title="Pauza nastavena ručně">⚙</span>' : ''}</h3>`
        : `<h3>${title}${badge}</h3>`;
    return `
        <section class="rep-block${isHidden ? ' is-hidden' : ''}${isDiscard ? ' is-discard' : ''}" data-block-original-idx="${originalIdx}">
            <header class="rep-block-head">
                ${(admin && !isDiscard) ? `<span class="rep-block-grip" draggable="true" data-block-grip="${originalIdx}" title="Tahem přesuň blok">⋮⋮</span>` : ''}
                ${titleHtml}
                <span class="rep-block-meta">
                    ${items.length} písní · ${fmtMin(Math.round(sec / 60))}
                    ${pauseMin > 0 ? `<span class="rep-pause${isLong ? ' long' : ''}${hasCustomPause ? ' custom' : ''}">pauza ${pauseMin} min</span>` : ''}
                </span>
                ${admin ? `
                    <button class="rep-block-add" data-add-song="${originalIdx}"
                            title="Vložit písničku z databáze repertoáru">+ Vložit</button>
                    ${!isDiscard ? `<button class="rep-block-toggle" data-toggle-hidden="${originalIdx}"
                            title="${isHidden ? 'Vrátit blok do živého repertoáru' : 'Schovat blok z PDF a souhrnu'}">
                        ${isHidden ? '▶ Vrátit do hraní' : '⏸ Schovat (záloha)'}
                    </button>` : ''}
                ` : ''}
            </header>
            ${(() => {
                const ol = `<ol class="rep-songs" data-block-idx="${originalIdx}">
                    ${items.map((it, pos) => renderItemHtml(it, admin, originalIdx, pos)).join('')}
                </ol>`;
                // Záložní bloky jsou sbalené — ať nezabírají místo
                if (!isHidden) return ol;
                const w = items.length === 1 ? 'píseň' : (items.length < 5 ? 'písně' : 'písní');
                return `<details class="rep-block-collapse">
                    <summary>${items.length} ${w} — rozbalit / sbalit</summary>
                    ${ol}
                </details>`;
            })()}
        </section>
    `;
}

// Vrátí pole zpěváků s fallbackem na starý 1-string sloupec `singer`.
function effectiveSingers(s) {
    if (Array.isArray(s?.singers) && s.singers.length) return s.singers.filter(Boolean);
    if (s?.singer) return [s.singer];
    return [];
}

function renderItemHtml(it, admin, blockOriginalIdx, position) {
    const s = it.song || {};
    const num = s.song_number != null ? s.song_number : '';
    const insHtml = formatInstruments(s.instruments);
    const dur = s.duration_sec ? fmtDuration(s.duration_sec) : '';
    const singers = effectiveSingers(s);
    const singerAttr = singers.length ? `data-singer="${escapeHtml([...singers].sort().join(','))}"` : '';
    const adminAttrs = admin
        ? `draggable="true"
           data-item-id="${it.id}"
           data-song-id="${s.id}"
           data-original-block="${blockOriginalIdx}"
           data-original-position="${position}"
           title="Dvojklik pro úpravu písně"`
        : '';
    const warnCls = [
        it._changeMichal ? 'warn-michal' : '',
        it._changeMatous ? 'warn-matous' : '',
        it._isDuplicate ? 'rep-song-dup' : ''
    ].filter(Boolean).join(' ');
    const ytHtml = s.youtube_url
        ? `<a class="rep-yt" href="${escapeHtml(s.youtube_url)}" target="_blank" rel="noopener" draggable="false" title="Přehrát na YouTube" aria-label="Přehrát na YouTube">▶</a>`
        : '<span class="rep-yt rep-yt-none" aria-hidden="true"></span>';
    return `
        <li class="rep-song${admin ? ' is-draggable' : ''}${warnCls ? ' ' + warnCls : ''}" ${singerAttr} ${adminAttrs}>
            ${admin ? '<span class="rep-grip" aria-hidden="true">⋮⋮</span>' : ''}
            <span class="rep-num">${escapeHtml(String(num))}</span>
            <span class="rep-title">${escapeHtml(s.title || '—')}${it._isDuplicate ? '<span class="rep-dup-badge" title="Tato písnička je v repertoáru víckrát">DUPLIKÁT</span>' : ''}</span>
            ${ytHtml}
            <span class="rep-ins">${insHtml}</span>
            <span class="rep-dur">${escapeHtml(dur)}</span>
        </li>
    `;
}

// ===== Zobrazení playlistu přes celou obrazovku (mobil) =====
// Stejný obsah jako export Tisk/PDF, jen vykreslený a zmenšený na obrazovku.

function openStageView(blockView, playlistName) {
    const musician = PDF_MUSICIANS.find((m) => m.id === pdfMusician);
    const footerName = _bandName ? `${_bandName} · ${playlistName}` : playlistName;
    const multi = document.getElementById('pdf-include-reserve')?.checked || false;
    const blocks = blockView
        .filter((b) => multi || !b.isHidden)
        .map((b) => b.items);

    const overlay = document.createElement('div');
    overlay.className = 'rep-stage-overlay';
    overlay.innerHTML = `
        <div class="stage-head">
            <div class="stage-head-title">${escapeHtml(playlistName)} · ${escapeHtml(musician?.label || '')}</div>
            <button class="stage-close" aria-label="Zavřít">✕</button>
        </div>
        <div class="stage-scroll">
            <div class="stage-page-wrap">
                <div class="rep-print-area${multi ? ' rep-multi' : ''}" id="stage-print-area">
                    <div class="rep-print-blocks${multi ? ' rep-pdf-multi' : ''}" id="stage-print-blocks">
                        ${renderPrintBlocks(blocks, footerName, multi)}
                    </div>
                </div>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);

    const area = overlay.querySelector('#stage-print-area');
    const wrap = overlay.querySelector('.stage-page-wrap');
    const scroll = overlay.querySelector('.stage-scroll');

    // V jednostránkovém režimu zmenši písmo, ať se vejde na A4.
    if (!multi) autofitArea(null, 'stage-print-blocks');

    // Stránku A4 zmenši tak, aby šířka sedla na obrazovku.
    const fit = () => {
        area.style.transform = 'none';
        const pageW = area.getBoundingClientRect().width;
        const pageH = area.getBoundingClientRect().height;
        const scale = Math.min(1, (scroll.clientWidth - 16) / pageW);
        area.style.transformOrigin = 'top left';
        area.style.transform = `scale(${scale})`;
        wrap.style.width = pageW * scale + 'px';
        wrap.style.height = pageH * scale + 'px';
    };
    fit();
    window.addEventListener('resize', fit);

    const close = () => {
        window.removeEventListener('resize', fit);
        overlay.remove();
    };
    overlay.querySelector('.stage-close').addEventListener('click', close);
    overlay.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
}

// ===== PDF / Tisk =====

function renderPrintAreaHtml(playlist, blocks) {
    // Pokud je nastaven název kapely, přidá se před název playlistu.
    const footerName = _bandName
        ? `${_bandName} · ${playlist.name}`
        : playlist.name;
    return `
        <div class="rep-print-area" id="rep-print-area">
            <div class="rep-print-blocks" id="rep-print-blocks">
                ${renderPrintBlocks(blocks, footerName)}
            </div>
        </div>
    `;
}

function renderPrintBlocks(blocks, playlistName = '', multi = false) {
    // Backward pass — varování pod nástrojem na POSLEDNÍ písni před přepnutím.
    const allItems = [];
    blocks.forEach((items) => allItems.push(...items));
    const flags = new Map();
    let nextInstr = null;
    for (let i = allItems.length - 1; i >= 0; i--) {
        const it = allItems[i];
        const cur = (it.song?.instruments || {})[pdfMusician] || null;
        flags.set(it, !!(cur && nextInstr && cur !== nextInstr));
        if (cur) nextInstr = cur;
    }

    // Víceřádkový režim: plynulý proud písní, CSS je naleje do 3 sloupců
    // přes column-count a podle potřeby i na další stránky.
    if (multi) {
        const seq = blocks.map((items, idx) => {
            const sep = idx > 0 ? '<div class="rpb-sep"></div>' : '';
            return sep + items.map((it) => renderPrintSong(it, flags.get(it))).join('');
        }).join('');
        const nameLine = playlistName
            ? `<div class="rep-print-footer">${escapeHtml(playlistName)}</div>`
            : '';
        return seq + nameLine;
    }

    // Rozděl bloky do 2 sloupců. Levý dostane o trochu víc položek,
    // aby sahal úplně až dolů (název playlistu pak sedí v pravém sloupci).
    const total = allItems.length;
    const targetLeft = Math.ceil(total * 0.52);
    const leftBlocks = [];
    const rightBlocks = [];
    let leftCount = 0;
    for (const items of blocks) {
        if (leftCount < targetLeft && leftBlocks.length === 0) {
            leftBlocks.push(items); leftCount += items.length;
        } else if (leftCount + items.length <= targetLeft) {
            leftBlocks.push(items); leftCount += items.length;
        } else {
            rightBlocks.push(items);
        }
    }

    const renderCol = (blockArr) => blockArr.map((items, idx) => {
        const sep = idx > 0 ? '<div class="rpb-sep"></div>' : '';
        return sep + items.map((it) => renderPrintSong(it, flags.get(it))).join('');
    }).join('');

    // Název playlistu je integrovaný jako poslední řádek v pravém sloupci.
    const nameLine = playlistName
        ? `<div class="rep-print-footer">${escapeHtml(playlistName)}</div>`
        : '';

    return `
        <div class="rep-col">${renderCol(leftBlocks)}</div>
        <div class="rep-col">${renderCol(rightBlocks)}${nameLine}</div>
    `;
}

function renderPrintSong(it, isChange = false) {
    const s = it.song || {};
    const num = s.song_number != null ? s.song_number : '';
    const ins = (s.instruments || {})[pdfMusician] || '';
    const insAttr = ins ? `data-instr="${escapeHtml(ins)}"` : '';
    return `
        <div class="rps${isChange ? ' rps-change' : ''}">
            <span class="rps-n">${escapeHtml(String(num))}</span>
            <span class="rps-t">${escapeHtml(s.title || '')}</span>
            <span class="rps-i ins-${pdfMusician}" ${insAttr}>${escapeHtml(ins)}</span>
        </div>
    `;
}

function rebuildPrintArea(blocks, footerName = '', multi = false) {
    const inner = document.getElementById('rep-print-blocks');
    if (!inner) return;
    const area = document.getElementById('rep-print-area');
    inner.classList.toggle('rep-pdf-multi', multi);
    if (area) area.classList.toggle('rep-multi', multi);
    inner.innerHTML = renderPrintBlocks(blocks, footerName, multi);
    // Reset font-size, ať autofit zvládne příště správně
    inner.style.fontSize = '';
}

function rebuildColorPrintArea(blocks, footerName = '', multi = false) {
    const inner = document.getElementById('rep-print-blocks-color');
    if (!inner) return;
    const area = document.getElementById('rep-print-area-color');
    inner.classList.toggle('rep-pdf-multi', multi);
    if (area) area.classList.toggle('rep-multi', multi);
    inner.innerHTML = renderColorPrintBlocks(blocks, footerName, multi);
    inner.style.fontSize = '';
}

// Přesune tiskové oblasti pod <body>, aby šel zbytek aplikace při tisku
// schovat přes display:none (jinak vznikají prázdné stránky navíc).
function relocatePrintAreas(container) {
    document.querySelectorAll('body > #rep-print-area, body > #rep-print-area-color')
        .forEach((el) => el.remove());
    ['rep-print-area', 'rep-print-area-color'].forEach((id) => {
        const el = container.querySelector('#' + id);
        if (el) document.body.appendChild(el);
    });
}

// Změří kolik px představuje X mm na aktuální obrazovce/zařízení
function mmToPx(mm) {
    const ref = document.createElement('div');
    ref.style.cssText = `position:absolute; left:-9999px; height:${mm}mm; width:1px;`;
    document.body.appendChild(ref);
    const px = ref.getBoundingClientRect().height;
    ref.remove();
    return px;
}

function autofitArea(container, blocksId) {
    const blocks = document.getElementById(blocksId);
    if (!blocks) return;
    const targetPx = mmToPx(PDF_PAGE_HEIGHT_MM - PDF_FOOTER_HEIGHT_MM);
    let pt = PDF_FONT_PT_MAX;
    blocks.style.fontSize = pt + 'pt';
    while (pt > PDF_FONT_PT_MIN) {
        const h = blocks.getBoundingClientRect().height;
        if (h <= targetPx) break;
        pt -= PDF_FONT_PT_STEP;
        blocks.style.fontSize = pt + 'pt';
    }
}

// ===== Barevná interaktivní varianta PDF (prokliky na YouTube) =====

function renderColorPrintAreaHtml(playlist, blocks) {
    // Barevný export — čistě seznam písniček, bez záhlaví/zápatí.
    return `
        <div class="rep-print-area rep-print-area-color" id="rep-print-area-color">
            <div class="rep-print-blocks" id="rep-print-blocks-color">
                ${renderColorPrintBlocks(blocks, '')}
            </div>
        </div>
    `;
}

function renderColorPrintBlocks(blocks, playlistName = '', multi = false) {
    if (multi) {
        const seq = blocks.map((items, idx) => {
            const sep = idx > 0 ? '<div class="rpb-sep"></div>' : '';
            return sep + items.map(renderColorSong).join('');
        }).join('');
        const nameLine = playlistName
            ? `<div class="rep-print-footer">${escapeHtml(playlistName)}</div>`
            : '';
        return seq + nameLine;
    }
    const allItems = [];
    blocks.forEach((items) => allItems.push(...items));
    const total = allItems.length;
    const targetLeft = Math.ceil(total * 0.52);
    const leftBlocks = [];
    const rightBlocks = [];
    let leftCount = 0;
    for (const items of blocks) {
        if (leftCount < targetLeft && leftBlocks.length === 0) {
            leftBlocks.push(items); leftCount += items.length;
        } else if (leftCount + items.length <= targetLeft) {
            leftBlocks.push(items); leftCount += items.length;
        } else {
            rightBlocks.push(items);
        }
    }
    const renderCol = (blockArr) => blockArr.map((items, idx) => {
        const sep = idx > 0 ? '<div class="rpb-sep"></div>' : '';
        return sep + items.map(renderColorSong).join('');
    }).join('');
    const nameLine = playlistName
        ? `<div class="rep-print-footer">${escapeHtml(playlistName)}</div>`
        : '';
    return `
        <div class="rep-col">${renderCol(leftBlocks)}</div>
        <div class="rep-col">${renderCol(rightBlocks)}${nameLine}</div>
    `;
}

function renderColorSong(it) {
    const s = it.song || {};
    const num = s.song_number != null ? s.song_number : '';
    const singers = effectiveSingers(s);
    const singerAttr = singers.length
        ? `data-singer="${escapeHtml([...singers].sort().join(','))}"`
        : '';
    const yt = s.youtube_url
        ? `<a class="rps-yt" href="${escapeHtml(s.youtube_url)}" target="_blank" rel="noopener">▶ YouTube</a>`
        : '<span class="rps-yt-none">—</span>';
    return `
        <div class="rps rps-color" ${singerAttr}>
            <span class="rps-n">${escapeHtml(String(num))}</span>
            <span class="rps-t">${escapeHtml(s.title || '')}</span>
            ${yt}
        </div>
    `;
}

